import React, { useEffect, useRef, useState } from "react";
import SEO from "../components/seo";
import { Flex, Heading } from "rebass";
import { graphql } from "gatsby";
import Blur from "../components/blur";
import Fullpage from "../components/fullpage";
import FirstSlide from "../components/firstSlide";
import BackgroundSlide from "../components/backgroundSlide";
import Parallax from "../components/parallax";
import { useMediaQuery } from "react-responsive";
import theme from "../gatsby-plugin-theme-ui";
import { useColorMode } from "theme-ui";
import useFirstPose from "../hooks/useFirstPose";
import { isEmpty, pathOr } from 'ramda';
import BlurredText from '../components/blurredText';
import GatsbyImage from 'gatsby-image';

const Clients = ({ data }) => {
  const {
    parallax,
    clients,
    parallaxMobile,
    ogImage,
  } = data;

  const { blocks: { blockIcons }, title, subtitle, description } = clients;

  const [activeIcon, setActiveIcon] = useState(blockIcons[Math.floor(Math.random() * blockIcons.length)].id);

  const isTabletOrMobile = useMediaQuery({ maxWidth: theme.breakpoints[1] });
  const [colorMode] = useColorMode();

  const containerRef = useRef(null);
  const iconsListRef = useRef(null);

  const pose = useFirstPose();

  useEffect(() => {
    if (!isTabletOrMobile) {
      const container = containerRef.current;

      const onWheelListener = e => {
        if (iconsListRef.current.scrollTop > 0) {
          e.stopPropagation();
        }
      };

      container.addEventListener('wheel', onWheelListener);

      return () => {
        container.removeEventListener('wheel', onWheelListener);
      }
    }
  }, [containerRef, blockIcons, isTabletOrMobile]);

  return (
    <>
      <SEO title="Clients" image={ogImage}/>
      <Fullpage
        background={({ blurred }) => (
          <Blur active={blurred} grayscale={false} sx={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0, willChange: 'filter', overflow: 'hidden' }}>
            <BackgroundSlide
              mixedBg={<Parallax layers={isTabletOrMobile ? parallaxMobile.layers : parallax.layers} disabled={blurred || pose === 'hidden'} />}
            />
          </Blur>
        )}
        sections={[
          () => <FirstSlide title={title} subtitle={subtitle} description={description} pose={pose} />,
          () => <Flex
            flexDirection="column"
            alignItems="center"
            maxWidth={[null, null, 950, 1100]}
            mb={[5, 6, 0]}
            maxHeight="100%"
            paddingTop={[null, null, '60px']}
            paddingBottom={[null, null, '30px']}
            ref={containerRef}
          >
            <Heading as="h3" fontSize={[6, null, null, 7]} px={[4, null, 0]} mb={[3, 4, 5]} sx={{ textAlign: 'center' }}>
              Ils nous font confiance
            </Heading>
            <Flex
              flexWrap="wrap"
              justifyContent="center"
              px={[2, null, 0]}
              width={[320, 700, 'auto']}
              maxHeight="100%"
              sx={{
                overflow: [null, null, 'auto'],
                '-ms-overflow-style': 'none',
                'scrollbar-width': 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
              ref={iconsListRef}
            >
              {blockIcons.map(icon => {
                const image = pathOr({}, ['icon', 'internal', 'relationships', 'media', 'file', 'sharp', 'fixed'], icon)
                return (
                  <BlurredText
                    active={activeIcon === icon.id}
                    key={icon.id}
                    height={[null, 100]}
                    width={[0.5, 'auto']}
                    sx={{
                      '> div': {
                        height: '100%',
                        width: 'auto',
                        ...(!!icon.link?.uri ? { cursor: 'pointer' } : {}),
                        filter: colorMode === 'default' ? 'grayscale(1) invert(1)' : 'grayscale(1) invert(0)'
                      }
                    }}
                    mx={[0, 3, 4]}
                    px={[2, 0]}
                    my={[3, 2, 3]}
                    justifyContent={['center']}
                    alignItems={['center']}
                    onMouseEnter={() => setActiveIcon(icon.id)}
                    onMouseLeave={() => setActiveIcon(null)}
                    onClick={() => !!icon.link?.uri && window.open(icon.link.uri, '_blank')}
                  >
                    {!isEmpty(image) && <GatsbyImage fixed={image} imgStyle={{ objectFit: 'contain' }} />}
                  </BlurredText>
                );
              })}
            </Flex>
          </Flex>,
        ]}
      />
    </>
  )
};

export default Clients;

export const query = graphql`
  query {
    clients: nodeClients {
      title
      description: field_dark_description_brut
      subtitle: field_dark_sub_title
      blocks: relationships {
        blockIcons: field_icons {
          id
          link: field_link {
            uri
          }
          icon: relationships {
            internal: field_icon {
              relationships {
                media: field_media_image {
                  file: localFile {
                    sharp: childImageSharp {
                      fixed(quality: 50, height: 100) {
                        ...GatsbyImageSharpFixed_withWebp
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    parallax: configsJson(name: {eq: "actualite-mixed"}) {
      layers {
        depth
        overflow
        offsetY
        image {
          id
          childImageSharp {
            original {
              width
              height
            }
            fluid(maxWidth: 3840, quality: 80) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    }
    parallaxMobile: configsJson(name: {eq: "actualite-mobile"}) {
      layers {
        depth
        overflow
        image {
          id
          childImageSharp {
            original {
              width
              height
            }
            fluid(maxWidth: 1280, quality: 65) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    }
    ogImage: file(name: {eq: "og_actualite"}) {
      ...ogImageFragment
    }
  }
`
